/**
 * useVendors — Application Service Hooks
 *
 * Wraps the VendorPort in React Query hooks.
 * Components import from here — never from axios, vendorApi, or URLs.
 *
 * Accepts a `port` parameter (defaults to the real API adapter) so
 * tests can inject a mock without touching React Query internals.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vendorApiAdapter } from '../infrastructure/api/vendorApi';
import type { VendorPort } from '../application/ports/VendorPort';
import type {
  CreateVendorRequest,
  LiveTestRequest,
  TestConnectionRequest,
  TestResult,
  UpdateVendorRequest,
  RoutingServiceKey,
  VendorServiceRouting,
  VendorMapping,
} from '../domain/vendor';

const VENDORS_KEY = ['vendors'] as const;
const THIRD_PARTY_SYNC_KEY = ['vendor-third-party-sync'] as const;

function notifyDownstreamSyncFailures(
  data: { vendorMappingsExport?: { ok: true } | { ok: false; error: string }; thirdPartyConfigSync?: { ok: true } | { ok: false; error: string } },
  onFailure?: (message: string) => void,
): boolean {
  const ex = data.vendorMappingsExport;
  if (ex && ex.ok === false) {
    onFailure?.(`vendorMappings.json export failed: ${ex.error}`);
    return true;
  }
  const sync = data.thirdPartyConfigSync;
  if (sync && sync.ok === false) {
    onFailure?.(`third-party adapter config sync failed: ${sync.error}`);
    return true;
  }
  return false;
}

function upsertVendorInCache(qc: ReturnType<typeof useQueryClient>, vendor: VendorMapping): void {
  qc.setQueryData<VendorMapping[]>(VENDORS_KEY, (current) => {
    if (!current) return [vendor];
    const index = current.findIndex(item => item.vendorId === vendor.vendorId);
    if (index === -1) return [vendor, ...current];
    return current.map(item => item.vendorId === vendor.vendorId ? vendor : item);
  });
}

// ── Query ─────────────────────────────────────────────────────────────────

export function useVendorList(port: VendorPort = vendorApiAdapter) {
  return useQuery({
    queryKey: VENDORS_KEY,
    queryFn: () => port.listVendors(),
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useCreateVendor(
  port: VendorPort = vendorApiAdapter,
  onSuccess?: (data: VendorMapping) => void,
  onVendorJsonExportFailed?: (message: string) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateVendorRequest) => port.createVendor(req),
    onSuccess: (data) => {
      upsertVendorInCache(qc, data);
      if (notifyDownstreamSyncFailures(data, onVendorJsonExportFailed)) {
        qc.invalidateQueries({ queryKey: VENDORS_KEY });
        qc.invalidateQueries({ queryKey: THIRD_PARTY_SYNC_KEY });
        return;
      }
      qc.invalidateQueries({ queryKey: VENDORS_KEY });
      qc.invalidateQueries({ queryKey: THIRD_PARTY_SYNC_KEY });
      onSuccess?.(data);
    },
  });
}

export function useUpdateVendor(
  port: VendorPort = vendorApiAdapter,
  onSuccess?: (data: VendorMapping) => void,
  onVendorJsonExportFailed?: (message: string) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendorId, request }: { vendorId: string; request: UpdateVendorRequest }) =>
      port.updateVendor(vendorId, request),
    onSuccess: (data) => {
      upsertVendorInCache(qc, data);
      if (notifyDownstreamSyncFailures(data, onVendorJsonExportFailed)) {
        qc.invalidateQueries({ queryKey: VENDORS_KEY });
        qc.invalidateQueries({ queryKey: THIRD_PARTY_SYNC_KEY });
        return;
      }
      qc.invalidateQueries({ queryKey: VENDORS_KEY });
      qc.invalidateQueries({ queryKey: THIRD_PARTY_SYNC_KEY });
      onSuccess?.(data);
    },
  });
}

export function useDeleteVendor(
  port: VendorPort = vendorApiAdapter,
  onVendorJsonExportFailed?: (message: string) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vendorId: string) => port.deleteVendor(vendorId),
    onSuccess: (data) => {
      notifyDownstreamSyncFailures(data, onVendorJsonExportFailed);
      qc.invalidateQueries({ queryKey: VENDORS_KEY });
      qc.invalidateQueries({ queryKey: THIRD_PARTY_SYNC_KEY });
    },
  });
}

// ── Test connection mutations (stateless — no cache invalidation needed) ──

export function useTestConnection(port: VendorPort = vendorApiAdapter) {
  return useMutation({
    mutationFn: (req: TestConnectionRequest) => port.testConnection(req),
  });
}

export function useTestVendorConnection(port: VendorPort = vendorApiAdapter) {
  return useMutation({
    mutationFn: (vendorId: string) => port.testVendorConnection(vendorId),
  });
}

/**
 * useLiveTest — fires a full HTTP request to a vendor endpoint via admin-api
 * (`POST /api/vendors/live-test`) and returns both the connectivity result AND
 * the parsed response body, so the wizard can render the field-picker from real data.
 */
export function useLiveTest(port: VendorPort = vendorApiAdapter) {
  return useMutation({
    mutationFn: (req: LiveTestRequest) => port.liveTest(req),
  });
}

export function useVendorServiceRouting(serviceName: RoutingServiceKey, port: VendorPort = vendorApiAdapter) {
  return useQuery({
    queryKey: ['vendor-service-routing', serviceName],
    queryFn: () => port.getServiceRouting(serviceName),
  });
}

export function useUpdateVendorServiceRouting(
  port: VendorPort = vendorApiAdapter,
  onVendorJsonExportFailed?: (message: string) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: VendorServiceRouting) => port.updateServiceRouting(req.serviceName, req),
    onSuccess: (data, variables) => {
      if (notifyDownstreamSyncFailures(data, onVendorJsonExportFailed)) {
        qc.invalidateQueries({ queryKey: ['vendor-service-routing', variables.serviceName] });
        qc.invalidateQueries({ queryKey: VENDORS_KEY });
        qc.invalidateQueries({ queryKey: THIRD_PARTY_SYNC_KEY });
        return;
      }
      qc.invalidateQueries({ queryKey: ['vendor-service-routing', variables.serviceName] });
      qc.invalidateQueries({ queryKey: VENDORS_KEY });
      qc.invalidateQueries({ queryKey: THIRD_PARTY_SYNC_KEY });
    },
  });
}

const ADAPTER_CONFIG_ROUTING_KEY = ['adapter-config-routing'] as const;

export function useAdapterConfigRouting(label: string, port: VendorPort = vendorApiAdapter) {
  const normalized = label.trim() || 'default';
  return useQuery({
    queryKey: [...ADAPTER_CONFIG_ROUTING_KEY, normalized],
    queryFn: () => port.getAdapterConfigRouting(normalized),
  });
}

export function useUpdateAdapterConfigRouting(port: VendorPort = vendorApiAdapter) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { label: string; serviceKey: RoutingServiceKey; vendors: VendorServiceRouting['vendors'] }) =>
      port.updateAdapterConfigRouting(req.serviceKey, req.label, req.vendors),
    onSuccess: (_data, variables) => {
      const normalized = variables.label.trim() || 'default';
      qc.invalidateQueries({ queryKey: [...ADAPTER_CONFIG_ROUTING_KEY, normalized] });
    },
  });
}

export function useThirdPartyConfigSyncStatus(port: VendorPort = vendorApiAdapter) {
  return useQuery({
    queryKey: THIRD_PARTY_SYNC_KEY,
    queryFn: () => port.getThirdPartyConfigSyncStatus(),
  });
}

export function useSyncThirdPartyConfig(
  port: VendorPort = vendorApiAdapter,
  onFailure?: (message: string) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (configLabel?: string) => port.syncThirdPartyConfig(configLabel),
    onSuccess: (data) => {
      if (data.ok === false) {
        onFailure?.(data.error);
      }
      qc.invalidateQueries({ queryKey: THIRD_PARTY_SYNC_KEY });
      qc.invalidateQueries({ queryKey: VENDORS_KEY });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to sync third-party adapter config.';
      onFailure?.(message);
      qc.invalidateQueries({ queryKey: THIRD_PARTY_SYNC_KEY });
    },
  });
}

// ── Composite hook (convenience — used by VendorModal) ────────────────────

export interface VendorFormHooks {
  save: ReturnType<typeof useCreateVendor> | ReturnType<typeof useUpdateVendor>;
  testConn: ReturnType<typeof useTestConnection>;
  isEdit: boolean;
}

export function useVendorForm(
  isEdit: boolean,
  onSuccess: () => void,
  port = vendorApiAdapter,
  onVendorJsonExportFailed?: (message: string) => void,
) {
  const create = useCreateVendor(port, onSuccess, onVendorJsonExportFailed);
  const update = useUpdateVendor(port, onSuccess, onVendorJsonExportFailed);
  const testConn = useTestConnection(port);

  return {
    save: isEdit ? update : create,
    testConn,
    isEdit,
    saveError: isEdit
      ? (update.error as any)?.response?.data?.error ?? update.error?.message ?? null
      : (create.error as any)?.response?.data?.error ?? create.error?.message ?? null,
    isPending: isEdit ? update.isPending : create.isPending,
    mutate: isEdit
      ? ({ vendorId, request }: { vendorId: string; request: UpdateVendorRequest }) =>
          update.mutate({ vendorId, request })
      : (req: CreateVendorRequest) => create.mutate(req),
  };
}

export { TestResult };
